import { Button, IconButton, Notice, Text, UI, VStack, HStack } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Divider } from 'native-base';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../../../hooks/useToastCreator';
import { customerDetailPrivatePensionActions } from '../../../../../../store/customerDetail/privatePension/actions';
import {
  privatePensionCompanyConfigQuery,
  updatePrivatePensionSelectedWithCustomerInfo,
} from '../../../../../../store/customerDetail/privatePension/selectors';
import { PrivatePensionContractedTabProps } from '../../constants';

import { BeneficiaryForm } from './components/BeneficiaryForm';
import {
  BeneficiariesFormData,
  DEFAULT_BENEFICIARY,
  FORM_CONFIG,
  beneficiariesSchema,
  defaultErrorMessage,
  defaultSuccessMessage,
} from './constants';

export const ChangeBeneficiaries: React.FC<PrivatePensionContractedTabProps> = ({
  setSelectedTab,
}) => {
  const privatePensionSelectedWithCustomerInfo = useRecoilValue(
    updatePrivatePensionSelectedWithCustomerInfo
  );
  const { showToast, showToastFromError } = useToastCreator('private-pension-beneficiaries');

  const { data: companyConfig, loading: loadingConfig } = useRecoilQuery(
    privatePensionCompanyConfigQuery({
      companyName: privatePensionSelectedWithCustomerInfo?.privatePension?.insurer,
    })
  );

  const { normalizeBeneficiariesToSend, updatePrivatePensionBeneficiaries } =
    customerDetailPrivatePensionActions();

  const [isLoading, setIsLoading] = useState(false);

  const formMethods = useForm<BeneficiariesFormData>({
    ...FORM_CONFIG,
    resolver: zodResolver(beneficiariesSchema),
  });

  const formValues = formMethods.watch();
  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: 'beneficiaries',
  });

  const handleAddBeneficiary = () => {
    append(DEFAULT_BENEFICIARY);
    setTimeout(() => {
      formMethods.trigger('beneficiaries');
    }, 100);
  };

  const handleRemoveBeneficiary = (index: number) => {
    remove(index);
    setTimeout(() => {
      formMethods.trigger('beneficiaries');
    }, 100);
  };

  const handleSubmit = useCallback(() => {
    const beneficiaries = normalizeBeneficiariesToSend(formValues.beneficiaries);

    setIsLoading(true);

    const { privatePension, cpfCnpj } = privatePensionSelectedWithCustomerInfo;

    updatePrivatePensionBeneficiaries(privatePension.certificate, cpfCnpj, beneficiaries)
      .then(() => {
        showToast(...defaultSuccessMessage);
        setSelectedTab('list');
      })
      .catch((error) => {
        if (instanceOfErrorListResponse(error)) {
          (error as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }

        showToast(...defaultErrorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  const handleGoBack = () => setSelectedTab('list');

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const subscription = formMethods.watch((value, { name, type }) => {
      if (name && name.includes('participation') && type === 'change') {
        clearTimeout(timeoutId);

        timeoutId = setTimeout(() => {
          formMethods.trigger('beneficiaries');
        }, 200);
      }
    });

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [formMethods]);

  const isFormComplete = useMemo(() => {
    return formValues.beneficiaries?.every(
      (beneficiary) =>
        beneficiary.name &&
        beneficiary.birthDate &&
        beneficiary.relationship &&
        beneficiary.participation
    );
  }, [formValues]);

  const isParticipationValid = useMemo(() => {
    if (!formValues.beneficiaries?.length) {
      return false;
    }

    const totalParticipation = formValues.beneficiaries.reduce(
      (total, beneficiary) => total + Utils.Unmask.Percentage(beneficiary.participation),
      0
    );

    return totalParticipation === 100;
  }, [formValues]);

  const { isValid } = formMethods.formState;
  const canSubmit = isValid && isFormComplete && isParticipationValid;

  const beneficiariesLimit = companyConfig?.data?.beneficiariesLimit ?? 10;
  const beneficiariesRelationship = companyConfig?.data?.beneficiariesRelationship ?? [];

  return (
    <Box height={{ base: 'full', md: '5/6', lg: '5/6' }}>
      <FormProvider {...formMethods}>
        <HStack justifyContent="space-between" alignItems="center">
          <HStack space={Spacing['stack.nano']} alignItems="center">
            <IconButton
              icon="ArrowLeft"
              testId="return-button"
              iconColor={Color['brand.primary.darkest']}
              variant={'link'}
              onPress={handleGoBack}
              iconSize="xs"
            />
            <UI.Text variant="semibold" fontSize={Typography['fontsize.lg']}>
              Alterar Beneficiários
            </UI.Text>
          </HStack>
          <Button
            variant={'secondary'}
            onPress={handleAddBeneficiary}
            _text={{
              color: 'neutral.darkest',
              fontWeight: 'semibold',
            }}
            isDisabled={isLoading || fields.length >= beneficiariesLimit || loadingConfig}>
            NOVO BENEFICIÁRIO
          </Button>
        </HStack>

        <VStack my={Spacing['stack.xxs']}>
          <Notice
            type="warning"
            testId="warning-notice"
            backgroundColor={Utils.ColorToken.WithOpacity(
              'feedback.attention.lightest',
              'opacity.medium'
            )}
            textObjects={[
              { text: 'Atenção: ', variant: 'bold' },
              {
                text: 'Ao realizar alterações na lista de beneficiários, os beneficiários anteriormente cadastrados serão substituídos pelas novas informações fornecidas. Certifique-se de revisar todos os dados antes de confirmar a alteração.',
                variant: 'regular',
              },
            ]}
          />
        </VStack>

        <VStack space={Spacing['inline.xxs']} zIndex={10}>
          {fields.map((field, index) => (
            <BeneficiaryForm
              key={field.id}
              index={index}
              BadgeTitle={'Beneficiário ' + (index + 1)}
              onRemove={() => handleRemoveBeneficiary(index)}
              showRemoveButton={fields.length > 1}
              isLoadingConfig={loadingConfig}
              beneficiariesRelationship={beneficiariesRelationship}
            />
          ))}
        </VStack>

        <Divider />
        <HStack
          space={Spacing['stack.quarck']}
          justifyContent="space-between"
          mt={Spacing['stack.xs']}>
          <Button variant={'secondary'} onPress={handleGoBack} isDisabled={isLoading}>
            <Text variant="semibold" fontSize={Typography['fontsize.md']} color="neutral.darkest">
              VOLTAR
            </Text>
          </Button>
          <Button variant={'primary'} onPress={handleSubmit} isDisabled={!canSubmit || isLoading}>
            <Text variant="semibold" fontSize={Typography['fontsize.md']} color="neutral.white">
              CONTINUAR
            </Text>
          </Button>
        </HStack>
      </FormProvider>
    </Box>
  );
};
